import React, { useState } from "react";
import { useApi } from "../contexts/ApiContext";
import { generateTests } from "../services/api";
import { TestCaseResponse } from "../types";
import ResultsDisplay from "./ResultsDisplay";

const MainScreen: React.FC = () => {
  const { apiKey, removeApiKey } = useApi();
  const [userStory, setUserStory] = useState("");
  const [domain, setDomain] = useState("Banking");
  const [customDomain, setCustomDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TestCaseResponse | null>(null);
  const [error, setError] = useState("");

  const domainOptions = [
    "AI",
    "Banking",
    "Crypto",
    "E-commerce",
    "Education",
    "Finance",
    "Gaming",
    "Healthcare",
    "Insurance",
    "Real Estate",
    "Social Media",
    "Travel",
    "Other",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userStory.trim()) {
      setError("Please enter a user story");
      return;
    }

    if (domain === "Other" && !customDomain.trim()) {
      setError("Please specify the domain");
      return;
    }

    setLoading(true);
    setError("");
    setResults(null);

    try {
      console.log("Sending request to generate tests...");
      const finalDomain = domain === "Other" ? customDomain.trim() : domain;

      const response = await generateTests({
        apiKey: apiKey!,
        userStory: userStory.trim(),
        domain: finalDomain,
      });

      console.log("Received response from API:", response);
      setResults(response);
    } catch (err: any) {
      console.error("Error generating tests:", err);

      // Extract error message from response
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 429) {
        errorMessage =
          "API quota exceeded. Please wait or check your Gemini API plan.";
      } else if (err.response?.status === 404) {
        errorMessage =
          "Service temporarily unavailable. Please try again later.";
      } else if (err.response?.status === 504) {
        errorMessage =
          "Request timed out. Please try again with a shorter user story.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNewTest = () => {
    setResults(null);
    setError("");
    setUserStory("");
  };

  const handleReset = () => {
    setResults(null);
    setError("");
    setUserStory("");
    setDomain("Banking");
    setCustomDomain("");
  };

  const handleDomainChange = (selectedDomain: string) => {
    setDomain(selectedDomain);
    if (selectedDomain !== "Other") {
      setCustomDomain("");
    }
  };

  const handleLogoClick = () => {
    window.open("https://idavidov.eu", "_blank");
  };

  return (
    <div className="main-screen">
      <header className="header">
        <h1>Test Case Generator</h1>
        <img
          src="/idavidov.png"
          alt="Ivan Davidov"
          className="header-logo"
          onClick={handleLogoClick}
          title="Visit idavidov.eu"
        />
        <div className="header-actions">
          <button
            onClick={handleReset}
            className="reset-button"
            title="Reset Form"
          >
            Reset
          </button>
          <button
            onClick={removeApiKey}
            className="logout-button"
            title="Change API Key"
          >
            Change API Key
          </button>
        </div>
      </header>

      {!results ? (
        <div className="form-container">
          <form onSubmit={handleSubmit} className="test-form">
            <div className="form-group">
              <label htmlFor="domain">Application Domain</label>
              <select
                id="domain"
                value={domain}
                onChange={(e) => handleDomainChange(e.target.value)}
                className="domain-select"
                disabled={loading}
              >
                {domainOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {domain === "Other" && (
              <div className="form-group">
                <label htmlFor="customDomain">Custom Domain</label>
                <input
                  id="customDomain"
                  type="text"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  placeholder="Enter your application domain (e.g., Manufacturing, Logistics)"
                  className="custom-domain-input"
                  disabled={loading}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="userStory">User Story</label>
              <textarea
                id="userStory"
                value={userStory}
                onChange={(e) => setUserStory(e.target.value)}
                placeholder="Add User Story (e.g., As a user, I want to login to my account so that I can access my dashboard)"
                className="user-story-textarea"
                rows={8}
                disabled={loading}
                required
              />
            </div>

            {error && (
              <div className="error-alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            <button
              type="submit"
              className="generate-button"
              disabled={
                loading ||
                !userStory.trim() ||
                (domain === "Other" && !customDomain.trim())
              }
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Generating Test Cases...
                </>
              ) : (
                "Generate Test Cases"
              )}
            </button>

            {loading && (
              <div className="loading-info">
                <p>
                  This may take 15-30 seconds depending on the complexity of
                  your user story.
                </p>
              </div>
            )}
          </form>
        </div>
      ) : (
        <ResultsDisplay results={results} onNewTest={handleNewTest} />
      )}

      <footer className="footer">
        <div className="footer-content">
          <p>
            This project is open source and created by I. Davidov. Find more
            about my work at{" "}
            <a
              href="https://idavidov.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              idavidov.eu
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MainScreen;
